import type { FootprintValue } from '~/types/person/footprint';

/**
 * What a reader calls each kind of exposed credential. One source, because the gate's
 * heading, the gate's control and the drawer's announcement all name the same thing, and
 * three copies of the noun is how they drift apart.
 */
export const credentialNoun = (fieldType: string) => (fieldType === 'ssn' ? 'Social Security number' : 'password');

/** The same noun where a sentence starts with it. */
export const capitalisedCredentialNoun = (fieldType: string) => {
    const noun = credentialNoun(fieldType);

    return noun.charAt(0).toUpperCase() + noun.slice(1);
};

/**
 * Whether the source classed a credential as a hash rather than as typed. It reveals like any
 * other value, because the classifier misreads some typed passwords as digests (a random
 * base64 string, or the base64 of a password), but it is never presented as the password:
 * every surface that names it calls it a hash. One test, because the gate, the drawer's header
 * and status region, and the result row's pill and accessible name all make this call, and a
 * copy of it in any one of them is how that one goes back to calling a hash the password.
 */
export const isHashedCredential = (value: FootprintValue | null) => value?.password_class?.kind === 'hashed';

/**
 * How every announcement about a credential the source classed as a hash begins. The drawer's
 * status region and the result row's accessible name each finish the sentence with where the
 * value stands, so each of them says "hash" in every state, and two copies of the opening is
 * how they drift apart.
 */
export const hashedCredentialNotice = (fieldType: string) =>
    `${capitalisedCredentialNoun(fieldType)} exposed on the dark web as a hash`;

/**
 * What a reader is told when the API refuses them a credential because it is not theirs: a
 * company admin who runs an employee's scan keeps the control, and this is the answer to
 * pressing it. One sentence for the toast and the drawer's own status region, which is the
 * only one heard while the dialog is open. It names a hash a hash, as the gate does.
 */
export const revealSelfOnlyNotice = (value: FootprintValue | null) => {
    const subject = isHashedCredential(value) ? 'hash' : credentialNoun(value?.field_type ?? '');

    return `For their security, only the person it belongs to can see this ${subject}.`;
};

/**
 * What a reader with no handle is told about a credential. The handle is the only thing the
 * reveal endpoint accepts, so there is no ask to offer them. One sentence for the drawer's status
 * region and the result row's accessible name, which both say it of the same fragment.
 */
export const ownerOnlyNotice = (value: FootprintValue | null) =>
    `The ${isHashedCredential(value) ? 'hash' : 'value'} itself is only ever shown to the person it belongs to.`;
